// /league/:id — one link for the whole league. Shows the entry page while seats fill, the army
// builder once it starts, then the league itself (games, standings, your army, host tools).
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { getLeague, act, touchLeague } from '../../leagues/api';
import type { LeagueView } from '../../leagues/api';
import { LeagueHeader, MemoryNote, storedName, storeName } from './parts';
import LeagueEntry, { RecoverForm } from './LeagueEntry';
import LeagueBuild from './LeagueBuild';
import LeagueHome from './LeagueHome';
import '../site/site.css';

export type Act = (action: string, body?: Record<string, unknown>) => Promise<LeagueView | null>;

export default function LeaguePage() {
  const { id } = useParams<{ id: string }>();
  const [league, setLeague] = React.useState<LeagueView | null>(null);
  const [missing, setMissing] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try { const L = await getLeague(id!); setLeague(L); setMissing(null); if (L.me) touchLeague(L.id, L.name); }
    catch (e) { setMissing((e as Error).message); }
  }, [id]);

  // Check for changes every few seconds while the page is open (results, joins, built armies).
  React.useEffect(() => {
    load();
    const t = window.setInterval(() => { if (document.visibilityState === 'visible') load(); }, 5000);
    return () => window.clearInterval(t);
  }, [load]);

  // One change at a time: while it's saving, the page's buttons wait (no double joins or picks).
  const [busy, setBusy] = React.useState(false);
  const busyRef = React.useRef(false);
  const doAct: Act = async (action, body = {}) => {
    if (busyRef.current) return null;
    busyRef.current = true; setBusy(true);
    setError(null);
    try {
      const L = await act(id!, action, body);
      setLeague(L);
      // A seat got back on a new device: this browser takes the league name if it has none yet.
      const me = L.me ? L.players.find(p => p.id === L.me!.id) : undefined;
      if (action === 'recover' && me && !storedName()) storeName(me.name);
      return L;
    } catch (e) {
      // fetch throws a TypeError when the request never got an answer.
      setError(e instanceof TypeError ? "Couldn't reach the site. Check your connection and try again." : (e as Error).message);
      return null;
    } finally { busyRef.current = false; setBusy(false); }
  };

  const me = league?.me ? league.players.find(p => p.id === league.me!.id) : undefined;
  return (
    <div className="site" style={{ fontSize: 16 }}>
      <LeagueHeader name={me?.name} />
      <main aria-busy={busy} style={{ maxWidth: 1240, margin: '0 auto', padding: '4px 24px 48px', display: 'grid', gap: 20, ...(busy ? { cursor: 'progress' } : {}) }}>
        {busy && <p role="status" style={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 20, padding: '8px 16px', borderRadius: 999, background: 'var(--panel)', border: '1px solid var(--panel-line)', boxShadow: '0 4px 16px rgba(0,0,0,.4)' }}>Saving…</p>}
        {missing && !league && (
          <div className="site-panel" style={{ maxWidth: 560, display: 'grid', gap: 12 }}>
            <h1 className="site-display" style={{ fontSize: 32 }}>League not found</h1>
            <p>{missing}</p>
            <p><Link to="/">Back to the home page</Link></p>
          </div>
        )}
        {!league && !missing && <p className="site-muted">Loading the league…</p>}
        {league && league.storage === 'memory' && <MemoryNote />}
        {/* Errors stay in view wherever you are on the page, until closed or the next change. */}
        {error && (
          <p role="alert" style={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 21, maxWidth: 'min(560px, calc(100vw - 32px))', display: 'flex', gap: 12, alignItems: 'center', padding: '10px 14px', borderRadius: 10, background: '#4a2a26', color: '#ffb3a8', boxShadow: '0 4px 16px rgba(0,0,0,.4)' }}>
            <span style={{ flex: 1 }}>{error}</span>
            <button type="button" className="site-icon-btn" aria-label="Close" onClick={() => setError(null)}>✕</button>
          </p>
        )}
        <div style={{ display: 'contents', ...(busy ? { pointerEvents: 'none' } : {}) }}>
        {league && league.status === 'open' && <LeagueEntry league={league} act={doAct} />}
        {league && !league.me && league.status !== 'open' && (
          <div className="site-panel" style={{ padding: '12px 16px', display: 'grid', gap: 6 }}>
            <p className="site-muted">You can follow this league from here. Already playing in it, on another device?</p>
            <RecoverForm act={doAct} open />
          </div>
        )}
        {league && league.status === 'building' && <LeagueBuild league={league} act={doAct} />}
        {league && (league.status === 'playing' || league.status === 'done') && <LeagueHome league={league} act={doAct} />}
        </div>
      </main>
    </div>
  );
}
